import { showAppModule } from './actions';
import { AppModules, PayloadShowAppModule } from './types';
import { FeatureContext, featureOperations, Features } from '@ardoq/features';
import { featureContext$ } from 'streams/featureContext$';
import { combineLatest, distinctUntilChanged, map } from 'rxjs';
import {
  persistentDerivedStream,
  persistentReducedStream,
  reducer,
} from '@ardoq/rxbeach';
import {
  PermissionContext,
  permissionsOperations,
} from '@ardoq/access-control';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';

/**
 * Modules where discover scope is preserved rather than reset when navigating.
 * Navigating to any other module resets scope.
 * Note: DISCOVER_LANDING_PAGE is also handled explicitly in isDiscoverScopeAfterNavigation
 * to set the scope to 'discover', making its entry here unreachable.
 */
const DISCOVER_MODULES: Set<AppModules> = new Set([
  AppModules.DISCOVER_LANDING_PAGE,
  AppModules.COMPONENT_OVERVIEW_PAGE,
  AppModules.DASHBOARDS,
  AppModules.REPORTS,
  AppModules.TASKS,
  AppModules.PRESENTATIONS,
  AppModules.INVENTORY,
  AppModules.WORKSPACES,
]);

export const isDiscoverScopeAfterNavigation = (
  isDiscover: boolean,
  { selectedModule }: PayloadShowAppModule
): boolean => {
  if (selectedModule === AppModules.DISCOVER_LANDING_PAGE) return true;
  if (DISCOVER_MODULES.has(selectedModule)) return isDiscover;
  return false;
};

const isDiscoverScope$ = persistentReducedStream('isDiscoverScope$', false, [
  reducer(showAppModule, isDiscoverScopeAfterNavigation),
]);

export type SidebarScope = 'core' | 'discover';

export const toSidebarScope = (
  isDiscover: boolean,
  featureContext: FeatureContext,
  permissionContext: PermissionContext
): SidebarScope => {
  if (!permissionsOperations.hasReaderRolePlus(permissionContext)) {
    return 'discover';
  }
  return isDiscover &&
    featureOperations.hasFeature(
      featureContext,
      Features.DISCOVER_CORE_LANDING_PAGE_AND_NAVIGATION
    ) &&
    featureOperations.hasFeature(
      featureContext,
      Features.DISCOVER_SCOPED_NAVIGATION
    )
    ? 'discover'
    : 'core';
};

/**
 * The current sidebar scope, used as the single source of truth by the
 * navigation menu, breadcrumbs, and the isDiscoverJourney stream.
 *
 * Emits 'discover' when the user has navigated to Discover and both
 * DISCOVER_CORE_LANDING_PAGE_AND_NAVIGATION and DISCOVER_SCOPED_NAVIGATION
 * are enabled. Emits 'core' in all other cases, except when the user is
 * a contributor who only has access to Discover.
 */
export const sidebarScope$ = persistentDerivedStream<SidebarScope>(
  'sidebarScope$',
  combineLatest([
    isDiscoverScope$,
    featureContext$,
    currentUserPermissionContext$,
  ]).pipe(
    map(([isDiscover, featureContext, permissionContext]) =>
      toSidebarScope(isDiscover, featureContext, permissionContext)
    ),
    distinctUntilChanged()
  ),
  'core'
);
